<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Invoices & Credits" backTo="internal">
      <is-authorized :permissions="['manage:credit']">
        <router-link
            :to="{ name: 'credit-create' }"
            class="flex items-center bg-indigo-200 border hover:bg-indigo-300 border-indigo-400 hover:border-indigo-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1 mr-2"
        >
          <svg
              class="stroke-current h-5 w-5 mr-2"
              fill="none"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              viewBox="0 0 24 24"
          >
            <path d="M12 5v14M5 12h14"/>
          </svg>
          <span>Create Open Credit</span>
        </router-link>
      </is-authorized>
      <router-link
          :to="{ name: 'invoice-create' }"
          class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M12 5v14M5 12h14"/>
        </svg>
        <span>Create Invoice</span>
      </router-link>
    </page-header>

    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <!-- Toolbar with Tab Headers and Search Controls -->
        <div class="w-full flex items-center justify-between">
          <div class="flex -mx-0 mr-2">
            <TabHeaders :tabs="tabs" :selectedTab="currentTab" @tab-selected="onTabSelected"/>
          </div>
          <div class="flex flex-col flex-1">
            <div class="flex justify-end">
              <div class="flex items-center mr-4">
                <label class="mr-2">From Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="fromDateInput"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>
              <div class="flex items-center ">
                <label class="mr-2">To Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="toDateInput"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>


              <input
                  type="text"
                  class="ml-6 w-full lg:w-auto rounded-l bge-input bge-input-spacing"
                  placeholder="Search"
                  v-model="searchTextInput"
                  @keydown.enter="triggerSearch"
              />
              <button
                  @click="triggerSearch"
                  class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
              >
                <Icon iconType="search"/>
              </button>
            </div>
          </div>
        </div>

        <!-- Tab Content -->
        <TabContent :currentTab="currentTab">
          <template v-slot:order_to_invoice>
            <DataTableComponent :columns="sales_columns" @row-click="onOrderRowClick" rowGroupMode="rowspan"
                                :key="currentTab" :rowClass="rowClass"
            >

              <template v-slot:status-slot="{ rowData }">
                {{ checkStatusAndReturnUppercase(rowData.status) }}
              </template>

              <template v-slot:order-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.order_date) }}
              </template>

              <template v-slot:dispatched-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.completed_date) }}
              </template>

              <template v-slot:total-value-slot="{ rowData }">
                {{ formatMoney(rowData.total_value) }}
              </template>


              <template v-if="!currentTabState.enableExpander" v-slot:action-slot="{ rowData }">
                <div class="flex justify-end space-x-2">

                  <AppButton  color="blue" label="" :icon="true"
                             @click.native.stop="convertToInvoice(rowData.sales_order_id)">
                    <template v-slot:icon>
                      <Icon iconType="file-invoice"/>
                    </template>
                  </AppButton>

                </div>
              </template>
            </DataTableComponent>
          </template>

          <template v-slot:invoices>
            <DataTableComponent
                :columns="invoice_columns"
                @row-click="onInvoiceRowClick"
                :key="currentTab"
                :rowClass="rowClass"
            >
              <!-- Status Slot -->
              <template v-slot:status-slot="{ rowData }">
                <span :class="getStatusClass(rowData.status)">
                  {{ checkStatusAndReturnUppercase(rowData.status) }}
                </span>
              </template>

              <!-- Invoice Date Slot -->
              <template v-slot:invoice-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.invoice_date) }}
              </template>

              <!-- Overdue Slot: worked out from the due date, the stored status is left alone -->
              <template v-slot:overdue-slot="{ rowData }">
                <span v-if="overdueLabel(rowData)" class="text-red-500 font-bold">{{ overdueLabel(rowData) }}</span>
              </template>

              <template v-slot:total-value-slot="{ rowData }">
                {{ formatMoney(rowData.total_value) }}
              </template>

              <!-- Actions Slot -->
              <template v-slot:action-slot="{ rowData }">
                <div class="flex justify-end space-x-2">
                  <!-- Edit Invoice -->
                  <AppButton
                      color="blue"
                      label=""
                      :icon="true"
                      @click.native.stop="editInvoice(rowData.invoice_id)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="edit" />
                    </template>
                  </AppButton>

                  <!-- Cancel Invoice -->
                  <AppButton
                      v-if="canCancelInvoice(rowData)"
                      color="red"
                      label=""
                      :icon="true"
                      @click.native.stop="cancelInvoice(rowData.invoice_id)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="circle-minus" />
                    </template>
                  </AppButton>
                </div>
              </template>
            </DataTableComponent>
          </template>

          <template v-slot:credit>
            <DataTableComponent
                :columns="credit_columns"
                @row-click="onCreditRowClick"
                :key="currentTab"
                :rowClass="rowClass"
            >
              <!-- Status Slot -->
              <template v-slot:status-slot="{ rowData }">
      <span :class="getStatusClass(rowData.status)">
        {{ checkStatusAndReturnUppercase(rowData.status) }}
      </span>
              </template>

              <!-- Credit Date Slot -->
              <template v-slot:credit-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.credit_date) }}
              </template>

              <template v-slot:total-value-slot="{ rowData }">
                {{ formatMoney(rowData.total_value) }}
              </template>

              <!-- Credit Expiry Slot -->
              <template v-slot:credit-expiry-slot="{ rowData }">
                <span v-if="rowData.credit_source !== 'invoice_correction'" :class="{ 'text-red-600 font-semibold': isExpired(rowData.credit_expiry_date) }">
                  {{ formatExpiry(rowData.credit_expiry_date) }}
                </span>
              </template>

              <!-- Actions Slot -->
              <template v-slot:action-slot="{ rowData }">
                <div class="flex justify-end space-x-2">
                  <!-- Edit Credit -->
                  <AppButton
                      color="blue"
                      label=""
                      :icon="true"
                      @click.native.stop="editCredit(rowData.credit_id)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="edit" />
                    </template>
                  </AppButton>

                  <!-- Cancel Credit -->
                  <AppButton
                      v-if="canVoidCredit(rowData)"
                      color="red"
                      label=""
                      :icon="true"
                      @click.native.stop="cancelCredit(rowData.credit_id)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="circle-minus" />
                    </template>
                  </AppButton>

                  <!-- Delete Credit -->
                  <AppButton
                      v-if="canDeleteCredit(rowData)"
                      color="red"
                      label=""
                      :icon="true"
                      @click.native.stop="deleteCredit(rowData.credit_id)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="delete" />
                    </template>
                  </AppButton>
                </div>
              </template>
            </DataTableComponent>
          </template>

          <template v-slot:warranty>
            <DataTableComponent
                :columns="invoice_columns"
                @row-click="onInvoiceRowClick"
                :key="currentTab"
                :rowClass="rowClass"
            >
              <!-- Status Slot -->
              <template v-slot:status-slot="{ rowData }">
                <span :class="getStatusClass(rowData.status)">
                  {{ checkStatusAndReturnUppercase(rowData.status) }}
                </span>
              </template>

              <!-- Invoice Date Slot -->
              <template v-slot:invoice-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.invoice_date) }}
              </template>

              <!-- Overdue Slot: worked out from the due date, the stored status is left alone -->
              <template v-slot:overdue-slot="{ rowData }">
                <span v-if="overdueLabel(rowData)" class="text-red-500 font-bold">{{ overdueLabel(rowData) }}</span>
              </template>

              <!-- Actions Slot -->
              <template v-slot:action-slot="{ rowData }">
                <div class="flex justify-end space-x-2">
                  <!-- Edit Invoice -->
                  <AppButton
                      color="blue"
                      label=""
                      :icon="true"
                      @click.native.stop="editInvoice(rowData.invoice_id)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="edit" />
                    </template>
                  </AppButton>

                  <!-- Cancel Invoice -->
                  <AppButton
                      v-if="canCancelInvoice(rowData)"
                      color="red"
                      label=""
                      :icon="true"
                      @click.native.stop="cancelInvoice(rowData.invoice_id)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="circle-minus" />
                    </template>
                  </AppButton>
                </div>
              </template>
            </DataTableComponent>
          </template>


        </TabContent>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import Panel from "@/components/Panel.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import TabContent from "@/components/TabContent.vue";
import DataTableComponent from "@/views/HR/Invoice/Components/DataTableComponent.vue";
import {mapActions, mapState} from "vuex";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";
import StaffAutocomplete from "@/components/AutoComplete/StaffAutoComplete.vue";
import AppButton from "@/components/Button.vue";

const IsAuthorized = () => import("@/components/Auth/IsAuthorized.vue");

export default {
  mixins: [notificationMixin],
  components: {
    AppButton,
    PageHeader,
    Spinner,
    Panel,
    TabHeaders,
    TabContent,
    DataTableComponent,
    Icon,
    StaffAutocomplete,
    IsAuthorized,
  },
  data() {
    return {
      tabs: [
        {id: "order_to_invoice", label: "Orders To Invoice",},
        {id: "invoices", label: "Invoices"},
        {id: "credit", label: "Credits",},
        {id: "warranty", label: "Warranty",},
      ],


    };
  },

  computed: {
    ...mapState("invoice", ["currentTab", "searchText", "fromDate", "toDate","lastSelectedRowId"]),

    ...mapState("invoice", {
      currentTabState: state => state.tabs[state.currentTab],
    }),


    sales_columns() {
      // Base columns
      const columns = [
        {
          field: "sales_order_no",
          header: "Order No.",
          sortable: true,
          custom: false,
          width: '10%',
        },
        {
          field: "order_date",
          header: "Order Date",
          sortable: true,
          custom: true,
          slotName: "order-date-slot",
          width: '12%',
        },
        {
          field: "dispatched_date",
          header: "Despatched Date",
          sortable: true,
          custom: true,
          slotName: "dispatched-date-slot",
          width: '12%',
        },
        {
          field: "sold_to_contact.name",
          header: "Sold To",
          sortable: true,
          custom: false,
          width: '15%',
        },
        {
          field: "invoice_to_contact.name",
          header: "Invoice To",
          sortable: true,
          custom: false,
          width: '15%',
        },
        {
          field: "total_value",
          header: "Order Total",
          sortable: true,
          custom: true,
          slotName: "total-value-slot",
          width: '10%',
          align: 'right',
          headerClass: 'column-h-text-right'
        },
        {
          field: "order_types",
          header: "",
          width: '1%',
        },
        {
          field: "order_type",
          header: "Order Type",
          sortable: true,
          custom: false,
          width: '9%',

        },
        {
          field: "status",
          header: "Status",
          sortable: true,
          custom: true,
          slotName: "status-slot",
          width: '9%',
        },
        {
          field: "action",
          header: "",
          sortable: false,
          custom: true,
          slotName: "action-slot",
          width: '7%',
        },
      ];
      return columns;
    },

    invoice_columns() {
      return [
        {
          field: "invoice_no",
          header: "Invoice No.",
          sortable: true,
          custom: false,
          width: "10%",
        },
        {
          field: "sales_order_no",
          header: "Order No.",
          sortable: true,
          custom: false,
          width: "10%",
        },
        {
          field: "invoice_date",
          header: "Invoice Date",
          sortable: true,
          custom: true, // Custom slot
          slotName: "invoice-date-slot",
          width: "12%",
        },
        {
          field: "sold_to_contact.name",
          header: "Sold To",
          sortable: true,
          custom: false,
          width: "15%",
        },
        {
          field: "invoice_to_contact.name",
          header: "Invoice To",
          sortable: true,
          custom: false,
          width: "15%",
        },
        {
          field: "total_value",
          header: "Order Total",
          sortable: true,
          custom: true,
          slotName: "total-value-slot",
          width: '10%',
          align: 'right',
          headerClass: 'column-h-text-right'
        },
        {
          field: "order_types",
          header: "",
          width: '1%',
        },
        {
          field: "invoice_type",
          header: "Invoice Type",
          sortable: true,
          custom: false,
          width: "9%",
        },
        {
          field: "status",
          header: "Status",
          sortable: true,
          custom: true, // Custom slot
          slotName: "status-slot",
          width: "9%",
        },
        {
          field: "payment_due_date",
          header: "Overdue",
          sortable: true,
          custom: true, // Custom slot
          slotName: "overdue-slot",
          width: "9%",
        },
        {
          field: "action",
          header: "",
          sortable: false,
          custom: true, // Custom slot
          slotName: "action-slot",
          width: "9%",
        },
      ];
    },

    credit_columns() {
      return [
        {
          field: "credit_no",
          header: "Credit No.",
          sortable: true,
          custom: false,
          width: "7%",
        },
        {
          field: "invoice_no",
          header: "Invoice No.",
          sortable: true,
          custom: false,
          width: "7%",
        },
        {
          field: "sales_order_no",
          header: "Order No.",
          sortable: true,
          custom: false,
          width: "7%",
        },
        {
          field: "credit_date",
          header: "Credit Date",
          sortable: true,
          custom: true, // Custom slot
          slotName: "credit-date-slot",
          width: "12%",
        },
        {
          field: "sold_to_contact.name",
          header: "Sold To",
          sortable: true,
          custom: false,
          width: "15%",
        },
        {
          field: "invoice_to_contact.name",
          header: "Invoice To",
          sortable: true,
          custom: false,
          width: "15%",
        },
        {
          field: "total_value",
          header: "Credit Value",
          sortable: true,
          custom: true,
          slotName: "total-value-slot",
          width: '10%',
          align: 'right',
          headerClass: 'column-h-text-right'
        },
        {
          field: "order_types",
          header: "",
          width: '1%',
        },
        {
          field: "credit_type",
          header: "Credit Type",
          sortable: true,
          custom: false,
          width: "8%",
        },
        {
          field: "status",
          header: "Status",
          sortable: true,
          custom: true, // Custom slot
          slotName: "status-slot",
          width: "9%",
        },
        {
          field: "credit_expiry_date",
          header: "Expiry",
          sortable: true,
          custom: true, // Custom slot
          slotName: "credit-expiry-slot",
          width: "9%",
        },
        {
          field: "action",
          header: "",
          sortable: false,
          custom: true, // Custom slot
          slotName: "action-slot",
          width: "9%",
        },
      ];
    },



    searchTextInput: {
      get() {
        return this.searchText;
      },
      set(value) {
        this.$store.commit("invoice/UPDATE_SEARCH_TEXT", value);
      },
    },

    selectedEngineerInput: {
      get() {
        return this.selectedEngineer;
      },
      set(value) {
        this.$store.commit("invoice/UPDATE_SELECTED_ENGINEER", value);
      },
    },

    fromDateInput: {
      get() {
        const fromDate = this.fromDate;
        return fromDate ? new Date(fromDate) : null;
      },
      set(value) {
        this.$store.commit("invoice/UPDATE_FROM_DATE", value);
      },
    },

    toDateInput: {
      get() {
        const toDate = this.toDate;
        return toDate ? new Date(toDate) : null;
      },
      set(value) {
        this.$store.commit("invoice/UPDATE_TO_DATE", value);
      },
    },
  },
  mounted() {
    this.fetchTableData();
  },
  methods: {
    ...mapActions("invoice", [
      "fetchTableData",
      "updateSearchCriteria",
      "updateTab",
      "resetCurrentPage",
    ]),

    async convertToInvoice(salesOrderId) {
      const confirmation = await this.confirmAction({
        message: 'Do you want to convert this sales order to an invoice?',
        header: 'Confirm Action',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
      });

      if (confirmation) {
        try {
          if (salesOrderId) {
            const newInvoice = await this.InvoiceService.convertFromSalesOrder(salesOrderId);
            this.notifySuccess("Sales order converted to invoice successfully");
            await this.$router.push({name: "invoice-edit", params: {invoice_id: newInvoice.invoice_id}});
            await this.fetchTableData(); // Refresh the table data after successful conversion
            this.$store.commit("invoice/SET_LAST_SELECTED_ROW", newInvoice.invoice_id);
          } else {
            this.notifyError("Sales order ID is missing");
          }
        } catch (error) {
          this.notifyError("Couldn't convert the sales order to an invoice");
          console.error(error);
        }
      }
    },


    async editInvoice(invoiceId) {
      await this.$router.push({name: "invoice-edit", params: {invoice_id: invoiceId}});
    },

    async cancelInvoice(invoiceId) {
      const confirmation = await this.confirmAction({
        message: "Cancel this invoice? It stays on record marked Cancelled, and no longer counts towards the customer's balance. It can't be edited or credited afterwards.",
        header: "Cancel Invoice",
        acceptLabel: "Yes, cancel it",
        rejectLabel: "No",
      });

      if (confirmation) {
        try {
          await this.InvoiceService.cancelInvoice(invoiceId);
          this.notifySuccess("Invoice cancelled");
          await this.fetchTableData();
        } catch (error) {
          const serverMessage = error && error.data && error.data.message;
          this.notifyError(serverMessage ? String(serverMessage) : "Failed to cancel the invoice");
          console.error(error);
        }
      }
    },



    async editCredit(creditId) {
      await this.$router.push({name: "credit-edit", params: {credit_id: creditId}});
    },

    async cancelCredit(creditId) {
      const confirmation = await this.confirmAction({
        message: "Void this credit? It stays on record marked Void, and can no longer be used or issued.",
        header: "Void Credit",
        acceptLabel: "Yes, void it",
        rejectLabel: "No",
      });

      if (confirmation) {
        try {
          await this.CreditService.voidCredit(creditId);
          this.notifySuccess("Credit voided");
          await this.fetchTableData();
        } catch (error) {
          const serverMessage = error && error.data && error.data.message;
          this.notifyError(serverMessage ? String(serverMessage) : "Failed to void the credit");
          console.error(error);
        }
      }
    },

    async deleteCredit(creditId) {
      const confirmation = await this.confirmAction({
        message: "Permanently delete this credit? This cannot be undone. If it should stay on record, void it instead.",
        header: "Delete Credit",
        acceptLabel: "Yes, delete it",
        rejectLabel: "No",
      });

      if (confirmation) {
        try {
          await this.CreditService.deleteCredit(creditId);
          this.notifySuccess("Credit deleted");
          await this.fetchTableData();
        } catch (error) {
          const serverMessage = error && error.data && error.data.message;
          this.notifyError(serverMessage ? String(serverMessage) : "Failed to delete the credit");
          console.error(error);
        }
      }
    },


    getStatusClass(status) {
      switch ((status || "").toUpperCase()) {
        case "OVERDUE":
        case "EXPIRED":
        case "VOID":
          return "text-red-500 font-bold";
        case "UNDER QUERY":
        case "PRO-FORMA":
          return "text-orange-500 font-bold";
        case "PAID":
        case "ISSUED":
        case "USED":
          return "text-green-500 font-bold";
        case "ACTIVE":
        case "PARTLY USED":
          return "text-blue-500 font-bold";
        case "PENDING":
          return "text-black font-bold";
        case "CANCELLED":
          return "text-gray-500 font-bold";
        default:
          return "text-black";
      }
    },

    validateDates() {
      const hasFromDate = !!this.fromDateInput;
      const hasToDate = !!this.toDateInput;

      if (hasFromDate && hasToDate) {
        if (this.toDateInput < this.fromDateInput) {
          this.notifyWarning("To Date should not be earlier than From Date.");
          return false;
        }
        return true;
      }

      if (hasFromDate !== hasToDate) {
        this.notifyWarning("Please select both the From Date and To Date.");
        return false;
      }

      return true;
    },

    onTabSelected(newTab) {

      this.updateTab(newTab);
    },

    triggerSearch() {
      if (this.validateDates()) {
        this.resetCurrentPage(0);
        this.updateTab(this.currentTab);
      }
    },


    async deleteDispatchNote(sales_order_id,dispatchNote) {

      const confirmation = await this.confirmAction({
        message: 'Do you want to delete this despatch note?',
        header: 'Confirm Action',
        acceptLabel: 'Yes',
        rejectLabel: 'No'
      });

      if (confirmation) {
        try {
          // Check if the dispatch note can be deleted

          if (dispatchNote && dispatchNote.status === 'not packed') {
            await this.SalesOrderService.deleteDispatchNote(sales_order_id, dispatchNote.dispatch_note_id);
            this.notifySuccess("Despatch note deleted successfully");
            this.updateTab(this.currentTab);
          } else {
            this.notifyError("Cannot delete this despatch note. Its current status is not allowed for deletion.");
          }
        } catch (error) {
          this.notifyError("Couldn't delete the despatch note");
          console.error(error);
        }
      }
    },

    editDispatchNote(sales_order_id,data) {

      this.$router.push({
        name: 'dispatch',
        params: {
          sales_order_id: sales_order_id,
          dispatch_note_id: data.dispatch_note_id
        }
      });
    },

    onOrderRowClick(event) {

      this.$store.commit("invoice/SET_LAST_SELECTED_ROW", event.data.sales_order_id);

      this.$router.push({
        name: 'sales-edit',
        params: {sales_order_id: event.data.sales_order_id}
      });
    },
    onInvoiceRowClick(event) {


      this.$store.commit("invoice/SET_LAST_SELECTED_ROW", event.data.sales_order_id);

      this.$router.push({
        name: 'invoice-edit',
        params: {invoice_id: event.data.invoice_id}
      });
    },
    onCreditRowClick(event) {

      this.$store.commit("invoice/SET_LAST_SELECTED_ROW", event.data.credit_id);

      this.$router.push({
        name: 'credit-edit',
        params: {credit_id: event.data.credit_id}
      });
    },
    formatTimestamp(unixTimestamp) {

      if (!unixTimestamp) {
        return ''; // or any default value like 'N/A'
      }
      return this.$moment.unix(unixTimestamp).format('DD-MM-YYYY');
    },
    formatMoney(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return '£0.00'; // Default value if input is invalid
      }

      // Ensure the value is treated as a float and format it
      const formattedValue = parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      return `£${formattedValue}`;
    },
    // A credit can be voided while it hasn't been used or issued. Numbered documents are never
    // deleted: the only thing that can be deleted is an open credit still in draft with no amount
    // on it, which was never a document to anyone. The server enforces the same rules.
    canVoidCredit(credit) {
      const status = (credit.status || "").toLowerCase();
      const unused = !credit.applications || credit.applications.length === 0;
      // An issued credit note can be voided while its invoice is unpaid; the server checks that
      const issuedCreditNote = credit.credit_source === "invoice_correction" && status === "issued";
      return issuedCreditNote || (unused && ["draft", "under query", "active"].includes(status));
    },

    canDeleteCredit(credit) {
      const unused = !credit.applications || credit.applications.length === 0;
      const blank = Math.abs(Number(credit.total_value) || 0) < 0.005;
      return unused && blank && credit.credit_source === "open" && (credit.status || "").toLowerCase() === "draft";
    },

    // An invoice can be cancelled while it is unpaid; the server also refuses one that has credit
    // raised against it or applied to it, and says so
    canCancelInvoice(invoice) {
      return ["pending", "overdue", "under query"].includes((invoice.status || "").toLowerCase());
    },

    // Overdue is worked out here and never stored: an unpaid invoice with something still owed
    // whose due date has passed - the same test the account statement uses to split overdue from
    // current. The stored status is left exactly as it is.
    overdueLabel(invoice) {
      const status = (invoice.status || "").toLowerCase();
      if (status === "paid" || status === "cancelled" || status === "pro-forma" || invoice.is_pro_forma || !invoice.payment_due_date) {
        return "";
      }

      const stored = invoice.outstanding_balance;
      const outstanding = stored !== undefined && stored !== null
          ? stored
          : (invoice.total_value || 0) - (invoice.credited_amount || 0);
      if (outstanding <= 0 || invoice.payment_due_date >= this.$moment().unix()) {
        return "";
      }

      const days = this.$moment().startOf("day").diff(this.$moment.unix(invoice.payment_due_date).startOf("day"), "days");
      if (days <= 0) {
        return "Overdue";
      }
      return days + (days === 1 ? " day overdue" : " days overdue");
    },
    isExpired(expiry) {
      if (!expiry) {
        return false;
      }
      return this.$moment.unix(expiry).endOf('day').isBefore(this.$moment());
    },
    formatExpiry(expiry) {
      if (!expiry) {
        return '';
      }
      if (this.isExpired(expiry)) {
        return 'Expired';
      }
      const daysRemaining = this.$moment.unix(expiry).endOf('day').diff(this.$moment(), 'days');
      if (daysRemaining <= 0) {
        return 'Expires today';
      }
      return `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`;
    },

    checkStatusAndReturnUppercase(status) {

      if (status && status.toLowerCase() === 'items added') {
        return 'OPEN';
      }

      if (status && status.toLowerCase() === 'confirmed') {
        return 'OPEN';
      }

      return status ? status.toUpperCase() : 'N/A';
    },
    getDeliveryDateClass(rowData) {
      if (['part received', 'items added'].includes(rowData.status)) {
        return 'expiry-badge status-' + this.getDeliveryDateStatus(rowData.requested_delivery_date);
      }
      return 'expiry-badge status-normal'
    },
    getDeliveryDateStatus(requestedDeliveryDate) {
      const today = this.$moment();
      const deliveryDate = this.$moment.unix(requestedDeliveryDate);
      const daysDiff = deliveryDate.diff(today, "days");

      if (daysDiff < 0) return "row-red-light"; // Past due date
      if (daysDiff <= 14) return "row-orange-light"; // Due within 14 days
      return "normal"; // No specific color
    },

    rowClass(rowData) {
      const allowedStatuses = ["items added", "open",  'part dispatched','confirmed'];
      let classes = [];

      if (allowedStatuses.includes(rowData.status)) {
        const rowColor = this.getDeliveryDateStatus(rowData.requested_delivery_date);
        if (rowColor === "row-red-light") {
          classes.push("row-red-light");
        } else if (rowColor === "row-orange-light") {
          classes.push("row-orange-light");
        }
      }

      if (rowData.invoice_id === this.lastSelectedRowId) {
        // classes.push("highlight-row");
      }

      return classes.join(" ");
    },
    getTotalItems(rowData) {
      const counts = this.calculateTotalCounts(rowData);
      return counts.totalItems;
    },

    getDispatchedCounts(rowData) {
      const counts = this.calculateTotalCounts(rowData);
      return counts.totalDispatched;
    },

    calculateTotalCounts(data) {
      if (!data || !data.items || !data.items.length) {
        return { totalItems: 0, totalDispatched: 0 };
      }

      let totalItems = 0;
      let totalDispatched = 0;

      // Calculate total items from order items
      data.items.forEach(item => {
        totalItems += parseFloat(item.quantity || 0);
      });

      // Calculate total dispatched quantities from dispatch notes
      if (data.dispatch_notes && data.dispatch_notes.length) {
        data.dispatch_notes.forEach(note => {
          if (note.status === 'dispatched') {
            note.items.forEach(item => {
              totalDispatched += parseFloat(item.quantity || 0); // Sum dispatched item quantities
            });
          }
        });
      }

      return { totalItems, totalDispatched };
    },

    showCancelOrderButton(status) {
      const statuses = ['open','part dispatched','completed','confirmed'];
      return statuses.includes(status);
    },
    showCopyOrderButton(status) {
      const statuses = ['part dispatched','completed', 'cancelled', 'items added', 'open','confirmed'];
      return statuses.includes(status);
    },

    async cancelOrder(salesOrderId) {
      const confirmation = await this.confirmAction({
        message: 'Do you want to cancel this order?',
        header: 'Confirm Action',
        acceptLabel: 'Yes',
        rejectLabel: 'No'
      });

      if (confirmation) {
        try {
          const cancelledDate = this.$moment().unix();

          const updateData = {
            status: 'cancelled',
            cancelled_date: cancelledDate,
          };

          if (salesOrderId) {
            await this.SalesOrderService.updateSalesOrder(
                salesOrderId,
                updateData,
            );

            await this.fetchTableData()
            this.notifySuccess("Purchase order cancelled successfully");

          } else {
            this.notifyError("Purchase order ID is missing");
          }

        } catch (error) {
          this.notifyError("Couldn't cancel the purchase order");
          console.error(error);
        } finally {

        }
      }
    },

    async copyOrder(salesOrderId) {
      const confirmation = await this.confirmAction({
        message: 'Do you want to copy this order?',
        header: 'Confirm Action',
        acceptLabel: 'Yes',
        rejectLabel: 'No'
      });

      if (confirmation) {
        try {
          if (salesOrderId) {
            const newOrder = await this.SalesOrderService.copySalesOrder(salesOrderId);
            this.notifySuccess("Sales order copied successfully");

            await this.fetchTableData()
            this.$store.commit("invoice/SET_LAST_SELECTED_ROW", newOrder.sales_order_id);

          } else {
            this.notifyError("Purchase order ID is missing");
          }

        } catch (error) {
          this.notifyError("Couldn't copy the purchase order");
          console.error(error);
        } finally {
        }
      }
    },
    async deleteOrder(salesOrderId) {
      const confirmation = await this.confirmAction({
        message: 'Do you want to delete this order?',
        header: 'Confirm Action',
        acceptLabel: 'Yes',
        rejectLabel: 'No'
      });

      if (confirmation) {
        try {
          if (salesOrderId) {
            await this.SalesOrderService.deleteSalesOrder(salesOrderId);
            this.notifySuccess("Sales order deleted successfully");
            await this.fetchTableData()
            // this.$router.go(-1);
          } else {
            this.notifyError("Sales order ID is missing");
          }

        } catch (error) {
          this.notifyError("Couldn't delete the sales order");
          console.error(error);
        } finally {
        }
      }
    },
  },
};
</script>

<style scoped>

::v-deep .row-red-light {
  color: red !important; /* Light shade of red */
}

::v-deep .row-orange-light {
  color: orange !important; /* Light shade of orange */
}

::v-deep .highlight-row {
  background-color: rgba(0,0,0,.15) !important;
}

::v-deep .column-h-text-left .p-column-header-content {
  text-align: left;
  display: block !important;
}

::v-deep .column-h-text-center .p-column-header-content {
  text-align: center;
  display: block !important;
}

::v-deep .column-h-text-right .p-column-header-content {
  text-align: right;
  display: block !important;
}

</style>
